import { Component, Input, OnInit } from '@angular/core';
import { NgbActiveModal } from '@ng-bootstrap/ng-bootstrap';
import { EndpointService } from '../../../../../../@shared/endpoints.service';
import { FamilyMemberResponse, UserSearchResult } from '../../../../../../@shared/models/response.module';
import { ToastService } from '../../../../../../@core/services/toast.service';
import { TranslateService } from '@ngx-translate/core';
import { Subject } from 'rxjs';
import { debounceTime, distinctUntilChanged, switchMap } from 'rxjs/operators';

@Component({
  selector: 'app-add-family-member-modal',
  standalone: false,
  templateUrl: './add-family-member-modal.html',
  styleUrl: './add-family-member-modal.css'
})
export class AddFamilyMemberModal implements OnInit {
  @Input() familyId: number;
  @Input() companyId: string;

  memberType: 'existing' | 'child' = 'existing';

  // Existing user search
  userSearchQuery: string = '';
  userSearchResults: UserSearchResult[] = [];
  selectedUser: UserSearchResult | null = null;
  isSearching: boolean = false;
  private searchSubject = new Subject<string>();

  // Child fields
  childFirstName: string = '';
  childLastName: string = '';
  childDateOfBirth: string = '';
  childRidingClub: string = '';

  isSubmitting: boolean = false;
  errorMessage: string = '';
  submitted: boolean = false;

  constructor(
    public activeModal: NgbActiveModal,
    private endpointService: EndpointService,
    private toastService: ToastService,
    private translateService: TranslateService
  ) {}

  ngOnInit() {
    // Setup debounced user search
    this.searchSubject.pipe(
      debounceTime(300),
      distinctUntilChanged(),
      switchMap(query => {
        if (query.length < 2) {
          return [];
        }
        this.isSearching = true;
        return this.endpointService.aqh_eve_67({ search: query });
      })
    ).subscribe({
      next: (response: any) => {
        this.isSearching = false;
        this.userSearchResults = response?.data || [];
      },
      error: () => {
        this.isSearching = false;
        this.userSearchResults = [];
      }
    });
  }

  onMemberTypeChange() {
    // Reset fields when switching types
    this.selectedUser = null;
    this.userSearchQuery = '';
    this.userSearchResults = [];
    this.childFirstName = '';
    this.childLastName = '';
    this.childDateOfBirth = '';
    this.childRidingClub = '';
    this.errorMessage = '';
    this.submitted = false;
  }

  onUserSearch() {
    this.searchSubject.next(this.userSearchQuery);
  }

  selectUser(user: UserSearchResult) {
    this.selectedUser = user;
    this.userSearchQuery = user.full_name || `${user.first_name} ${user.last_name}`;
    this.userSearchResults = [];
  }

  clearSelectedUser() {
    this.selectedUser = null;
    this.userSearchQuery = '';
  }

  // Validation
  get isSelectedUserInvalid(): boolean {
    return this.submitted && this.memberType === 'existing' && this.selectedUser === null;
  }

  get isChildFirstNameInvalid(): boolean {
    return this.submitted && this.memberType === 'child' && !this.childFirstName.trim();
  }

  get isChildLastNameInvalid(): boolean {
    return this.submitted && this.memberType === 'child' && !this.childLastName.trim();
  }

  get isChildDateOfBirthInvalid(): boolean {
    return this.submitted && this.memberType === 'child' && !this.childDateOfBirth;
  }

  isValid(): boolean {
    if (this.memberType === 'existing') {
      return this.selectedUser !== null;
    } else {
      return this.childFirstName.trim().length > 0 &&
             this.childLastName.trim().length > 0 &&
             this.childDateOfBirth.length > 0;
    }
  }

  submit() {
    this.submitted = true;

    if (!this.isValid()) {
      this.toastService.error(this.translateService.instant('common.fillRequiredFields'));
      return;
    }

    this.isSubmitting = true;
    this.errorMessage = '';

    if (this.memberType === 'existing' && this.selectedUser) {
      // Add existing user by email
      this.endpointService.aqh_fam_1({
        family_id: this.familyId,
        email: this.selectedUser.email,
        role: 'guardian',
        company_id: this.companyId
      }).subscribe({
        next: (response) => {
          this.isSubmitting = false;
          this.toastService.success(
            this.translateService.instant('companyProfile.membershipPlans.editMember.familyMemberAdded')
          );
          this.activeModal.close(response.data);
        },
        error: (error) => {
          this.isSubmitting = false;
          this.errorMessage = error?.error?.reason ||
            this.translateService.instant('companyProfile.membershipPlans.editMember.familyMemberAddError');
          this.toastService.error(this.errorMessage);
        }
      });
    } else {
      // Add child
      this.endpointService.aqh_fam_2({
        family_id: this.familyId,
        first_name: this.childFirstName.trim(),
        last_name: this.childLastName.trim(),
        date_of_birth: this.childDateOfBirth,
        riding_club: this.childRidingClub.trim() || 'N/A',
        role: 'child',
        company_id: this.companyId
      }).subscribe({
        next: (response) => {
          this.isSubmitting = false;
          this.toastService.success(
            this.translateService.instant('companyProfile.membershipPlans.editMember.childAdded')
          );
          this.activeModal.close(response.data);
        },
        error: (error) => {
          this.isSubmitting = false;
          this.errorMessage = error?.error?.reason ||
            this.translateService.instant('companyProfile.membershipPlans.editMember.childAddError');
          this.toastService.error(this.errorMessage);
        }
      });
    }
  }
}
